import { Grid } from "@mui/material"
import ReactMarkdown from "react-markdown"
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { CodeViewer } from "./CodeViewer"
import { LoadingSpinner, useLoadingSpinner } from '../../Components/LoadingSpinner';
import { useAppDispatch, useAppSelector } from '../../Services/Store';
import { getWikiPageContent, selectPageContent, selectPageUrl, selectIsLoaded } from "../../Services/Store/Slices/WikiPageSlice";
import { selectIsLoaded as selectMenuIsLoaded } from "../../Services/Store/Slices/WikiSlice";
import { selectSelectedWiki } from "../../Services/Store/Slices/WikiSlice";
import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { Wiki, WikiPage } from "../../Services/Api";

function findWikiPage(wiki: Wiki, pageUrl: string): WikiPage | null {
  for (let i = 0; i < wiki.children.length; i++) {
    let wikiPage = findWikiPageNested(wiki.children[i], pageUrl);

    if (wikiPage !== null)
      return wikiPage;
  }
  return null;
}

function findWikiPageNested(wikiPage: WikiPage, pageUrl: string): WikiPage | null {
  if (normalizeUrl(wikiPage.fileUrl) === normalizeUrl(pageUrl))
    return wikiPage;

  for (let i = 0; i < wikiPage.children.length; i++) {
    let page = findWikiPageNested(wikiPage.children[i], pageUrl);

    if (page !== null)
      return page;
  }
  return null;
}

function normalizeUrl(url:string):string{
  return decodeURIComponent(url);
}

export const MarkdownViewer = () => {
  const { isLoading, startLoading, stopLoading } = useLoadingSpinner();
  const dispatch = useAppDispatch();
  const msalContextRef = useRef(useMsal());
  const isLoaded = useAppSelector(selectIsLoaded);
  const menuIsLoaded = useAppSelector(selectMenuIsLoaded);
  const pageContent = useAppSelector(selectPageContent);
  const selectedWiki = useAppSelector(selectSelectedWiki);
  const pageUrl = useAppSelector(selectPageUrl);

  useEffect(() => {
    if (selectedWiki !== null && !isLoaded && menuIsLoaded) {
      startLoading();
      
      let wikiPage = null;

      if (pageUrl?.endsWith('.md')) {
        wikiPage = findWikiPage(selectedWiki, pageUrl);
      } else {
        wikiPage = selectedWiki.children[0];
      }

      dispatch(getWikiPageContent({ context: msalContextRef.current, repository: selectedWiki.name, wikiPage: wikiPage }))
        .finally(() => stopLoading());
    }

  }, [isLoaded, selectedWiki, pageUrl, menuIsLoaded, dispatch, startLoading, stopLoading]);

  return(
    <Grid position='relative' overflow='auto'>
      <ReactMarkdown 
        children={ pageContent }
        remarkPlugins={ [remarkGfm] }
        remarkRehypeOptions={{ allowDangerousHtml: true }}
        rehypePlugins={ [rehypeRaw] }
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '')

            return !inline ? (
              <CodeViewer 
                children={ children } 
                language={ match === null || match.length !== 2 ? 'core' : match[1] }
              />
            ) : (
              <code className={ className } { ...props }>
                { children }
              </code>
            )
          }
        }}
      />
      {(isLoading && <LoadingSpinner />)}
    </Grid>
  );
}